"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SignUpSchema } from "../../auth.schema";
import { useSignUpMutation } from "../../auth.mutations";
import { SignUpType } from "../../auth.type";

interface SignUpFormProps {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const signUpFormSchema = SignUpSchema.omit({ instance: true }).extend({
  phone: SignUpSchema.shape.phone.or(z.literal("")).optional(),
  confirmPassword: z.string().min(4, "A confirmação de senha deve ter pelo menos 4 caracteres."),
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem.",
  path: ["confirmPassword"],
});

export function SignUpForm({ onSuccess, onError }: SignUpFormProps) {
  const signUpMutation = useSignUpMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<z.infer<typeof signUpFormSchema>>({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
  });

  const generateInstanceFromName = (name: string) => {
    const base = name.toLowerCase().replace(/[^a-z0-9]/g, "");
    const suffix = Math.random().toString(36).substring(2, 7);
    return `${base}${suffix}`.substring(0, 30);
  };

  async function onSubmit(values: z.infer<typeof signUpFormSchema>) {
    try {
      const generatedInstance = generateInstanceFromName(values.name);

      const payload: SignUpType = {
        name: values.name,
        email: values.email,
        phone: values.phone ? values.phone.replace(/\D/g, "") : undefined,
        password: values.password,
        instance: generatedInstance,
      };

      const response = await signUpMutation.mutateAsync(payload);

      if (response.status === "success") {
        onSuccess?.();
      } else {
        onError?.(response);
      }
    } catch (error: any) {
      console.error("Raw sign-up error:", error);
      onError?.(error);
    }
  }

  return (
    <Card className="rounded-2xl bg-gray-30 dark:bg-black/10 ">
      <CardHeader>
        <CardTitle className="text-2xl">Registre-se</CardTitle>
        <CardDescription>Preencha os campos abaixo para criar sua conta</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome</FormLabel>
                  <FormControl>
                    <Input placeholder="Seu Nome" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="exemplo@dominio.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Digite sua senha"
                        {...field}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        )}
                        <span className="sr-only">{showPassword ? "Ocultar senha" : "Mostrar senha"}</span>
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirme sua Senha</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirme sua senha"
                        {...field}
                        className="pr-10"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4" aria-hidden="true" />
                        ) : (
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        )}
                        <span className="sr-only">{showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}</span>
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full cursor-pointer" disabled={signUpMutation.isPending}>
              {signUpMutation.isPending ? "Criando Conta..." : "Criar Conta"}
            </Button>
          </form>
        </Form>

        <div className="mt-4 text-left text-sm">
          Já tem uma conta?{" "}
          <Link href="/sign-in" className="underline">
            Faça login
          </Link>
        </div>

        <div className="mt-6 text-center text-xs text-muted-foreground">
          <p>Ao continuar, você concorda com nossos</p>
          <Link href="/privacy" className="underline underline-offset-4 hover:text-primary">
            Política de Privacidade
          </Link>{" "}
          e{" "}
          <Link href="/terms" className="underline underline-offset-4 hover:text-primary">
            Termos de Uso
          </Link>
          .
        </div>
      </CardContent>
    </Card>
  );
}
